/**
 * Drilldown controller – panel only
 */
class DrawerDrilldownController {
  constructor(detailsEl, opts = {}) {
    this.details = detailsEl;

    this.HOVER_ENABLED_MIN_WIDTH = opts.hoverMinWidth ?? 990;
    this.OPEN_DELAY = opts.openDelay ?? 60;
    this.CLOSE_DELAY = opts.closeDelay ?? 90;

    this.state = { openT: null, closeT: null };
    this._attached = false;

    // hover guard po open
    this.hoverBlockUntil = 0;

    this.onClick = this.onClick.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseEnterCapture = this.onMouseEnterCapture.bind(this);

    // lazy resolve
    this.panel = null;
    this.root = null;
  }

  resolve() {
    this.panel = this.details.querySelector('[data-drawer-panel]');
    this.root = this.details.querySelector('[data-drill-root]');
    return Boolean(this.panel && this.root);
  }

  isHoverEnabled() {
    return window.matchMedia(`(min-width: ${this.HOVER_ENABLED_MIN_WIDTH}px)`).matches;
  }

  blockHover(ms = 300) {
    this.hoverBlockUntil = performance.now() + ms;
  }

  isHoverBlocked() {
    return performance.now() < this.hoverBlockUntil;
  }

  clearTimers() {
    const st = this.state;
    if (st.openT) clearTimeout(st.openT);
    if (st.closeT) clearTimeout(st.closeT);
    st.openT = null;
    st.closeT = null;
  }

  setShift(pct) {
    if (!this.root) return;
    this.root.style.setProperty('--dd-shift', `${pct}%`);
  }

  fillLevel(level, html) {
    if (!this.root) return;
    const panel = this.root.querySelector(`.level-${level}`);
    if (!panel) return;

    const body = panel.querySelector('[data-body]');
    if (body) body.innerHTML = html || '';

    panel.setAttribute('aria-hidden', 'false');
  }

  hideLevel(level) {
    if (!this.root) return;
    const panel = this.root.querySelector(`.level-${level}`);
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'true');

    const body = panel.querySelector('[data-body]');
    if (body) body.innerHTML = '';
  }

  closeAll() {
    this.hideLevel(2);
    this.hideLevel(1);
    this.setShift(0);
  }

  closeL2KeepL1() {
    this.hideLevel(2);
    this.setShift(100);
  }

  openL1(triggerEl) {
    if (!this.root) return;
    const id = (triggerEl.getAttribute('data-open-l1') || '').trim();
    if (!id) return;

    const tpl = this.root.querySelector(`template#${CSS.escape(id)}`);
    if (!tpl) return;

    this.fillLevel(1, tpl.innerHTML);
    this.hideLevel(2);
    this.setShift(100);
  }

  openL2(triggerEl) {
    if (!this.root) return;
    const id = (triggerEl.getAttribute('data-open-l2') || '').trim();
    if (!id) return;

    const tpl = this.root.querySelector(`template#${CSS.escape(id)}`);
    if (!tpl) return;

    this.fillLevel(2, tpl.innerHTML);
    this.setShift(200);
  }

  resetAll() {
    this.clearTimers();
    this.closeAll();
  }

  onClick(e) {
    const open1 = e.target.closest('[data-open-l1]');
    const open2 = e.target.closest('[data-open-l2]');
    const backBtn = e.target.closest('[data-back]');

    if (!open1 && !open2 && !backBtn) return;

    // anchors nawigują normalnie (chyba że mają data-no-navigate)
    if (open1 && open1.tagName === 'A' && !open1.hasAttribute('data-no-navigate')) return;
    if (open2 && open2.tagName === 'A' && !open2.hasAttribute('data-no-navigate')) return;

    e.preventDefault();
    e.stopPropagation();

    if (open1) return this.openL1(open1);
    if (open2) return this.openL2(open2);

    const lvl = Number(backBtn.getAttribute('data-back'));
    if (lvl === 2) return this.closeL2KeepL1();
    return this.closeAll();
  }

  onMouseOver(e) {
    if (!this.isHoverEnabled()) return;
    if (this.isHoverBlocked()) return;

    const item = e.target.closest('.drawer-item');
    if (!item || !this.panel || !this.panel.contains(item)) return;

    this.clearTimers();

    const inLevel0 = Boolean(item.closest('.level-0'));
    const inLevel1 = Boolean(item.closest('.level-1'));

    const open1 = item.closest('[data-open-l1]');
    const open2 = item.closest('[data-open-l2]');

    if (open1) {
      this.state.openT = setTimeout(() => this.openL1(open1), this.OPEN_DELAY);
      return;
    }
    if (open2) {
      this.state.openT = setTimeout(() => this.openL2(open2), this.OPEN_DELAY);
      return;
    }

    this.state.closeT = setTimeout(() => {
      if (inLevel0) this.closeAll();
      else if (inLevel1) this.closeL2KeepL1();
    }, this.CLOSE_DELAY);
  }

  onMouseEnterCapture(e) {
    if (!this.isHoverEnabled()) return;
    if (this.isHoverBlocked()) return;

    const blank = e.target?.closest('[data-blank]');
    if (!blank || !this.panel || !this.panel.contains(blank)) return;

    this.clearTimers();

    const lvl = blank.getAttribute('data-blank');
    this.state.closeT = setTimeout(() => {
      if (lvl === '0') this.closeAll();
      if (lvl === '1') this.closeL2KeepL1();
    }, this.CLOSE_DELAY);
  }

  attach() {
    if (this._attached) return;
    if (!this.resolve()) return;

    this.panel.addEventListener('click', this.onClick);
    this.panel.addEventListener('mouseover', this.onMouseOver);
    // jeśli kiedykolwiek znów będzie bug, to to jest pierwsze do wywalenia:
    this.panel.addEventListener('mouseenter', this.onMouseEnterCapture, true);

    this._attached = true;
  }

  detach() {
    if (!this._attached) return;

    if (this.panel) {
      this.panel.removeEventListener('click', this.onClick);
      this.panel.removeEventListener('mouseover', this.onMouseOver);
      this.panel.removeEventListener('mouseenter', this.onMouseEnterCapture, true);
    }

    this._attached = false;
    this.resetAll();

    this.panel = null;
    this.root = null;
  }
}

/**
 * Details drawer – built-in custom element (<details is="details-drawer">)
 * Źródło prawdy: natywny <details>. Reagujemy na "toggle".
 */
class DetailsDrawer extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = { bodyClass: 'has-left-drawer' };

    this.onToggle = this.onToggle.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.onInnerClick = (e) => {
      const closeBtn = e.target.closest('[data-drawer-close]');
      if (closeBtn) {
        e.preventDefault();
        this.open = false;
      }
    };

    this._anim = null;
    this.panel = null;
    this.overlay = null;
    this.drill = null;
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.overlay = this.querySelector('[data-drawer-overlay]');

    // drill (niech istnieje zawsze, ale attach dopiero przy open)
    this.drill = this.drill || new DrawerDrilldownController(this);

    this.addEventListener('toggle', this.onToggle);
    this.addEventListener('click', this.onInnerClick);

    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);

    // init (bez animacji)
    this.applyStateInstant(this.open);
  }

  disconnectedCallback() {
    this.removeEventListener('toggle', this.onToggle);
    this.removeEventListener('click', this.onInnerClick);

    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);

    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keyup', this.onKeyUp, true);

    this.drill?.detach?.();
    this._anim?.cancel?.();
    this._anim = null;
  }

  onOverlayClick(e) {
    e.preventDefault();
    this.open = false;
  }

  onKeyUp(e) {
    if (e.key === 'Escape' || e.code === 'Escape') this.open = false;
  }

  onDocumentClick(e) {
    if (!this.open) return;
    if (!this.contains(e.target)) this.open = false;
  }

  syncA11y(isOpen) {
    if (this.panel) this.panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    if (this.overlay) this.overlay.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  setOverlayInteractive(isOpen) {
    if (!this.overlay) return;
    // masz w klasach: invisible ... pointer-events-none
    this.overlay.classList.toggle('invisible', !isOpen);
    this.overlay.classList.toggle('pointer-events-none', !isOpen);
  }

  setPanelInteractive(isOpen) {
    if (!this.panel) return;
    this.panel.style.visibility = isOpen ? 'visible' : 'hidden';
    this.panel.style.pointerEvents = isOpen ? 'auto' : 'none';
  }

  applyStateInstant(isOpen) {
    if (!this.panel || !this.overlay) return;

    this.syncA11y(isOpen);
    this.setOverlayInteractive(isOpen);
    this.setPanelInteractive(isOpen);

    if (isOpen) {
      document.body.classList.add(this.classes.bodyClass);

      this.overlay.style.visibility = 'visible';
      this.overlay.style.opacity = '1';

      this.panel.style.opacity = '1';
      this.panel.style.transform = 'translateX(0)';

      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keyup', this.onKeyUp, true);

      this.drill?.attach?.();
      this.drill?.blockHover?.(300);
    } else {
      document.body.classList.remove(this.classes.bodyClass);

      this.overlay.style.opacity = '0';
      this.overlay.style.visibility = 'hidden';

      this.panel.style.opacity = '0';
      this.panel.style.transform = 'translateX(-105%)';

      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keyup', this.onKeyUp, true);

      this.drill?.detach?.();
    }
  }

  async onToggle() {
    const panel = this.panel;
    const overlay = this.overlay;
    if (!panel || !overlay) return;

    this._anim?.cancel?.();
    this._anim = null;

    const isOpen = this.open;

    // close others
    if (isOpen) {
      document.querySelectorAll('details[is="details-drawer"][open]').forEach((d) => {
        if (d !== this) d.open = false;
      });
    }

    this.syncA11y(isOpen);

    if (isOpen) {
      document.body.classList.add(this.classes.bodyClass);

      // show/interact first
      this.setOverlayInteractive(true);
      this.setPanelInteractive(true);

      overlay.style.visibility = 'visible';
      panel.style.visibility = 'visible';

      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keyup', this.onKeyUp, true);

      this.drill?.attach?.();
      this.drill?.blockHover?.(300);

      // start values for Motion
      overlay.style.opacity = '0';
      panel.style.opacity = '0';
      panel.style.transform = 'translateX(-105%)';

      if (window.Motion?.animate && !window.theme?.config?.motionReduced) {
        Motion.animate(overlay, { opacity: [0, 1] }, { duration: 0.2, easing: [.2, .8, .2, 1] });
        Motion.animate(panel, { opacity: [0, 1] }, { duration: 0.2, easing: [.2, .8, .2, 1] });
        this._anim = Motion.animate(panel, { transform: ['translateX(-105%)', 'translateX(0)'] }, { duration: 0.45, easing: [.075, .82, .165, 1] });
        await this._anim.finished;
      } else {
        overlay.style.opacity = '1';
        panel.style.opacity = '1';
        panel.style.transform = 'translateX(0)';
      }
    } else {
      document.body.classList.remove(this.classes.bodyClass);

      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keyup', this.onKeyUp, true);

      this.drill?.detach?.();

      if (window.Motion?.animate && !window.theme?.config?.motionReduced) {
        Motion.animate(overlay, { opacity: [1, 0] }, { duration: 0.15, easing: [.2, .8, .2, 1] });
        Motion.animate(panel, { opacity: [1, 0] }, { duration: 0.15, easing: [.2, .8, .2, 1] });
        this._anim = Motion.animate(panel, { transform: ['translateX(0)', 'translateX(-105%)'] }, { duration: 0.35, easing: [.075, .82, .165, 1] });
        await this._anim.finished;
      } else {
        overlay.style.opacity = '0';
        panel.style.opacity = '0';
        panel.style.transform = 'translateX(-105%)';
      }

      overlay.style.visibility = 'hidden';
      this.setOverlayInteractive(false);
      this.setPanelInteractive(false);
      this.syncA11y(false);
    }
  }
}

// define
if (!customElements.get('details-drawer')) {
  customElements.define('details-drawer', DetailsDrawer, { extends: 'details' });
}